import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import {
  getSupplier, updateSupplier, deleteSupplier, getSupplierCategories,
  saveSupplierMemberCard, deleteSupplierMemberCard, uploadSupplierCardPhoto,
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft, Save, Trash2, Link2, AlertTriangle, CreditCard, Camera,
  Maximize2, Building2, Percent, Receipt, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { UkAddressFields } from '../../components/UkAddressFields';
import { XeroAddressSyncBanner } from '../../components/XeroAddressSyncBanner';
import { SupplierBarChart, QuarterCompare } from '../../components/SupplierSpendChart';

const fmtMoney = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
import { formatDate } from '../../utils/date';

const fmtDate = (iso) => formatDate(iso);

const SupplierDetail = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCard, setShowCard] = useState(false);
  // Controlled tab — staying on the same tab across `reload()` is critical
  // (e.g. after saving a member-card the user expects to still be on the
  // Member card tab to see the preview, not get bounced back to Details).
  const [activeTab, setActiveTab] = useState('details');

  const reload = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        getSupplier(supplierId),
        getSupplierCategories(),
      ]);
      setSupplier(s);
      setOriginal(s);
      setCategories(c.categories || []);
    } catch (e) {
      toast.error('Failed to load supplier');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [supplierId]);

  const dirty = useMemo(() => {
    if (!supplier || !original) return false;
    const fields = [
      'name', 'category', 'email', 'phone', 'website',
      'addressLine1', 'addressLine2', 'addressLine3', 'city', 'county', 'postcode', 'country',
      'vatNumber', 'companyNumber', 'accountNumber', 'paymentTerms',
      'defaultDiscountRate', 'discountApplyMode', 'notes',
    ];
    return fields.some(f => (supplier[f] ?? '') !== (original[f] ?? ''));
  }, [supplier, original]);

  const update = (patch) => setSupplier(s => ({ ...s, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        name: supplier.name,
        category: supplier.category,
        email: supplier.email, phone: supplier.phone, website: supplier.website,
        addressLine1: supplier.addressLine1, addressLine2: supplier.addressLine2,
        addressLine3: supplier.addressLine3 || '', county: supplier.county || '',
        city: supplier.city, postcode: supplier.postcode, country: supplier.country,
        vatNumber: supplier.vatNumber, companyNumber: supplier.companyNumber,
        accountNumber: supplier.accountNumber,
        paymentTerms: Number(supplier.paymentTerms) || 30,
        defaultDiscountRate: Number(supplier.defaultDiscountRate) || 0,
        discountApplyMode: supplier.discountApplyMode || 'bill',
        notes: supplier.notes,
      };
      const updated = await updateSupplier(supplierId, patch);
      setSupplier(updated);
      setOriginal(updated);
      toast.success('Supplier saved');
      if (updated.xeroAddressSync?.status === 'failed' && updated.xeroAddressSync.at > (original?.xeroAddressSync?.at || '')) {
        toast.error(`Address saved in Lutrom, but Xero wasn't updated: ${updated.xeroAddressSync.error}`, { duration: 10000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete supplier "${supplier.name}"? This cannot be undone.`)) return;
    try {
      await deleteSupplier(supplierId);
      toast.success('Supplier deleted');
      navigate('/admin/suppliers');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading supplier…</div>;
  if (!supplier) return <div className="p-6 text-slate-500">Supplier not found.</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl" data-testid="admin-supplier-detail">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/admin/suppliers" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to suppliers
        </Link>
      </div>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl text-slate-900 truncate">{supplier.name}</h1>
              <Badge variant="outline">{supplier.category}</Badge>
              {supplier.xeroContactId && (
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
                  <Link2 className="h-3 w-3 mr-1" /> Linked to Xero
                </Badge>
              )}
              {supplier.defaultDiscountRate > 0 && (
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200">
                  <Percent className="h-3 w-3 mr-1" /> {(supplier.defaultDiscountRate * 100).toFixed(1)}% trade
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={remove} data-testid="supplier-delete-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
            <Button size="sm" onClick={save} disabled={!dirty || saving} data-testid="supplier-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>

        {/* AP totals */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <KpiBox label="Spent (12 mo.)" value={fmtMoney(supplier.spent)} accent="slate" />
          <KpiBox label="Outstanding" value={fmtMoney(supplier.outstanding)} accent="amber" />
          <KpiBox
            label="Overdue"
            value={fmtMoney(supplier.overdue)}
            accent={supplier.overdue > 0 ? 'rose' : 'slate'}
            warn={supplier.overdue > 0}
          />
        </div>

        {/* 12-month spend bars — use it to spot seasonality before
            negotiating next year's trade discount. Highlights the most
            recent quarter in blue, prior 9 months in grey. */}
        <div className="mt-6 pt-5 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-slate-500">12-month spend</div>
            <QuarterCompare data={supplier.monthlySpend} />
          </div>
          <SupplierBarChart data={supplier.monthlySpend} />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* On narrow viewports the four tab labels don't fit a 4-col grid
            (Trade account + Member card + Bills (0) all crash into each
            other). We give the list horizontal scroll on small screens
            and switch to a flex row so each tab keeps its natural width
            and gains a bit of padding for tap-targets. */}
        <TabsList className="flex w-full overflow-x-auto sm:grid sm:grid-cols-4 sm:w-auto sm:overflow-visible">
          <TabsTrigger value="details" className="flex-shrink-0 px-3" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="trade" className="flex-shrink-0 px-3" data-testid="tab-trade">Trade account</TabsTrigger>
          <TabsTrigger value="card" className="flex-shrink-0 px-3" data-testid="tab-card">Member card</TabsTrigger>
          <TabsTrigger value="bills" className="flex-shrink-0 px-3" data-testid="tab-bills">Bills ({supplier.recentBills?.length || 0})</TabsTrigger>
        </TabsList>

        {/* ---------- Details ---------- */}
        <TabsContent value="details" className="space-y-4 pt-4">
          <Section title="Contact">
            <Grid2>
              <Field label="Name" value={supplier.name} onChange={v => update({ name: v })} testId="field-name" />
              <SelectField
                label="Category"
                value={supplier.category}
                options={categories}
                onChange={v => update({ category: v })}
                testId="field-category"
              />
              <Field label="Email" value={supplier.email} onChange={v => update({ email: v })} />
              <Field label="Phone" value={supplier.phone} onChange={v => update({ phone: v })} />
              <Field label="Website" value={supplier.website} onChange={v => update({ website: v })} className="col-span-2" />
            </Grid2>
          </Section>

          <Section title="Address">
            <XeroAddressSyncBanner kind="supplier" doc={supplier} onDone={() => reload()} />
            <UkAddressFields
              value={{
                line1: supplier.addressLine1 || '', line2: supplier.addressLine2 || '', line3: supplier.addressLine3 || '',
                town: supplier.city || '', county: supplier.county || '', postcode: supplier.postcode || '',
                country: supplier.country || '',
              }}
              onChange={(p) => update({
                addressLine1: p.line1, addressLine2: p.line2, addressLine3: p.line3,
                city: p.town, county: p.county, postcode: p.postcode, country: p.country,
              })}
              testId="supplier-address"
            />
            {supplier.xeroContactId && (
              <p className="text-[11px] text-slate-500 mt-2">Saving a changed address also updates this supplier's Xero contact.</p>
            )}
          </Section>

          <Section title="Tax & Company">
            <Grid2>
              <Field label="VAT number" value={supplier.vatNumber} onChange={v => update({ vatNumber: v })} placeholder="GB123456789" />
              <Field label="Company number" value={supplier.companyNumber} onChange={v => update({ companyNumber: v })} />
            </Grid2>
          </Section>

          <Section title="Notes">
            <Textarea
              data-testid="field-notes"
              rows={3}
              value={supplier.notes || ''}
              onChange={e => update({ notes: e.target.value })}
              placeholder="Any internal notes — preferred contact, delivery quirks, account opening date…"
            />
          </Section>
        </TabsContent>

        {/* ---------- Trade Account ---------- */}
        <TabsContent value="trade" className="space-y-4 pt-4">
          <Section
            title="Trade account"
            subtitle="Your account number with this supplier and standard payment terms."
          >
            <Grid2>
              <Field
                label="Trade account number"
                value={supplier.accountNumber}
                onChange={v => update({ accountNumber: v })}
                placeholder="e.g. 1234567"
                testId="field-account-number"
              />
              <Field
                label="Payment terms (days)"
                type="number"
                value={supplier.paymentTerms ?? 30}
                onChange={v => update({ paymentTerms: parseInt(v) || 30 })}
              />
            </Grid2>
          </Section>

          <Section
            title="Standard trade discount"
            subtitle="Auto-applies to receipts/bills from this supplier when the AI parser misses it."
          >
            <Grid2>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Discount %</Label>
                <Input
                  data-testid="field-discount-rate"
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={(supplier.defaultDiscountRate * 100).toFixed(2).replace(/\.?0+$/, '') || 0}
                  onChange={e => update({
                    defaultDiscountRate: Math.max(0, Math.min(1, (parseFloat(e.target.value) || 0) / 100)),
                  })}
                  className="mt-1"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Stored as a decimal — e.g. 5% = 0.05. Capped at 100%.
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Apply mode</Label>
                <select
                  data-testid="field-discount-mode"
                  className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm bg-white"
                  value={supplier.discountApplyMode || 'bill'}
                  onChange={e => update({ discountApplyMode: e.target.value })}
                >
                  <option value="bill">Bill-level (single discount line, like the receipt prints it)</option>
                  <option value="line">Per-line (Xero DiscountRate on every line)</option>
                  <option value="ask">Ask me each time</option>
                </select>
              </div>
            </Grid2>
            {supplier.defaultDiscountRate > 0 && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                <Percent className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                When you open a bill from <span className="font-semibold">{supplier.name}</span>,
                BillDetail will offer a one-tap "Apply {(supplier.defaultDiscountRate * 100).toFixed(1)}% trade discount"
                button if the AI parser missed it.
              </div>
            )}
          </Section>
        </TabsContent>

        {/* ---------- Member Card ---------- */}
        <TabsContent value="card" className="space-y-4 pt-4">
          <MemberCardPanel supplier={supplier} reload={reload} onShow={() => setShowCard(true)} />
        </TabsContent>

        {/* ---------- Bill History ---------- */}
        <TabsContent value="bills" className="space-y-4 pt-4">
          <BillHistory bills={supplier.recentBills || []} />
        </TabsContent>
      </Tabs>

      <ShowCardDialog open={showCard} onOpenChange={setShowCard} supplier={supplier} />
    </div>
  );
};

// ---------- subcomponents ----------
const KpiBox = ({ label, value, accent, warn }) => {
  const cls = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  }[accent];
  return (
    <div className={`border rounded p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
      <div className="font-display text-xl tabular-nums mt-1 flex items-center gap-1.5">
        {warn && <AlertTriangle className="h-4 w-4" />}
        {value}
      </div>
    </div>
  );
};

const Section = ({ title, subtitle, children }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5">
    <div className="font-display uppercase tracking-wide text-sm text-slate-700 mb-1">{title}</div>
    {subtitle && <div className="text-xs text-slate-500 mb-3">{subtitle}</div>}
    <div className={subtitle ? '' : 'mt-3'}>{children}</div>
  </div>
);

const Grid2 = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder, className = '', testId }) => (
  <div className={className}>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <Input
      data-testid={testId}
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1"
    />
  </div>
);

const SelectField = ({ label, value, options, onChange, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <select
      data-testid={testId}
      className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm bg-white"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const MemberCardPanel = ({ supplier, reload, onShow }) => {
  const [format, setFormat] = useState(supplier.memberCardBarcodeFormat || 'CODE128');
  const [value, setValue] = useState(supplier.memberCardBarcodeValue || '');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const hasCard = !!(supplier.memberCardBarcodeValue || supplier.memberCardImageBase64);

  const saveBarcode = async () => {
    if (!value.trim()) return toast.error('Enter a barcode value');
    setSaving(true);
    try {
      await saveSupplierMemberCard(supplier.id, {
        barcodeValue: value.trim(),
        barcodeFormat: format,
      });
      toast.success('Member card saved');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Card image must be under 2MB');
      return;
    }
    setSaving(true);
    try {
      await uploadSupplierCardPhoto(supplier.id, file);
      toast.success('Card photo uploaded');
      reload();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Upload failed');
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeCard = async () => {
    if (!window.confirm('Remove member card?')) return;
    try {
      await deleteSupplierMemberCard(supplier.id);
      toast.success('Member card removed');
      reload();
    } catch (e) {
      toast.error('Remove failed');
    }
  };

  return (
    <div className="space-y-4">
      <Section
        title="Member / discount card"
        subtitle='Store the barcode value (preferred — re-renders crisp at any size) or a photo of the card. Tap "Show card" at the till for the cashier to scan.'
      >
        {hasCard && (
          <div className="bg-slate-50 border border-slate-200 rounded p-4 mb-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Saved card</div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onShow} data-testid="show-card-btn">
                  <Maximize2 className="h-4 w-4 mr-1" /> Show full-screen
                </Button>
                <Button variant="outline" size="sm" onClick={removeCard} className="text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <CardPreview supplier={supplier} compact />
          </div>
        )}

        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Option 1 — enter barcode value</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Format</Label>
                <select
                  data-testid="card-format"
                  className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm bg-white"
                  value={format}
                  onChange={e => setFormat(e.target.value)}
                >
                  <option value="CODE128">CODE128 (most common)</option>
                  <option value="EAN13">EAN-13</option>
                  <option value="EAN8">EAN-8</option>
                  <option value="UPC">UPC</option>
                  <option value="QR">QR code</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Card number / barcode value</Label>
                <Input
                  data-testid="card-value"
                  className="mt-1 font-mono"
                  value={value}
                  onChange={e => setValue(e.target.value)}
                  placeholder="e.g. 1234567890123"
                />
              </div>
            </div>
            <Button onClick={saveBarcode} disabled={saving} className="mt-3" data-testid="card-save-barcode">
              <CreditCard className="h-4 w-4 mr-2" /> Save barcode
            </Button>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Option 2 — upload a photo of the card</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
              data-testid="card-file-input"
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={saving}
              data-testid="card-photo-btn"
            >
              <Camera className="h-4 w-4 mr-2" /> {saving ? 'Uploading…' : 'Upload card photo'}
            </Button>
            <div className="text-[10px] text-slate-500 mt-2">
              Use this only if the card has no readable barcode value. Max 2MB.
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

const CardPreview = ({ supplier, compact }) => {
  const fmt = supplier.memberCardBarcodeFormat;
  const val = supplier.memberCardBarcodeValue;
  const img = supplier.memberCardImageBase64;

  if (fmt === 'PHOTO' && img) {
    return (
      <img
        src={img}
        alt={`${supplier.name} member card`}
        className={`mx-auto rounded ${compact ? 'max-h-48' : 'max-h-[60vh]'} object-contain max-w-full`}
        data-testid="card-preview-photo"
      />
    );
  }
  if (fmt === 'QR' && val) {
    return (
      // QR scales down on narrow viewports — the SVG keeps its aspect
      // ratio so the cashier scanner still reads it cleanly. compact view
      // (used in inline previews) caps it at 140px.
      <div className="flex justify-center" data-testid="card-preview-qr">
        <div
          className={`${compact ? 'max-w-[140px]' : 'max-w-full w-[320px]'} [&>svg]:w-full [&>svg]:h-auto`}
        >
          <QRCodeSVG value={val} size={320} level="M" includeMargin />
        </div>
      </div>
    );
  }
  if (val) {
    return (
      // The barcode SVG ships with hard-coded width attributes so on a
      // ~330px mobile dialog the standard size overflows the right edge.
      // Wrapping it with `[&>svg]:w-full [&>svg]:h-auto` makes the SVG
      // scale to its container while preserving aspect ratio. We let
      // it grow up to 600px on bigger screens so it stays readable.
      <div
        className="bg-white p-3 rounded mx-auto w-full max-w-[600px] [&>svg]:w-full [&>svg]:h-auto"
        data-testid="card-preview-barcode"
      >
        <Barcode
          value={val}
          format={fmt || 'CODE128'}
          width={compact ? 1.6 : 3}
          height={compact ? 60 : 120}
          fontSize={compact ? 12 : 18}
          margin={5}
        />
      </div>
    );
  }
  return <div className="text-sm text-slate-400 italic">No card on file.</div>;
};

const ShowCardDialog = ({ open, onOpenChange, supplier }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] sm:max-w-2xl p-4 sm:p-6"
      data-testid="show-card-dialog"
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 pr-8">
          <CreditCard className="h-5 w-5 shrink-0" />
          <span className="truncate">{supplier.name} — member card</span>
        </DialogTitle>
      </DialogHeader>
      <div className="bg-white rounded p-2 sm:p-4 overflow-hidden">
        <CardPreview supplier={supplier} />
      </div>
      <div className="text-xs text-center text-slate-500">
        Show this to the cashier — they'll scan the barcode at the till.
      </div>
    </DialogContent>
  </Dialog>
);

const BillHistory = ({ bills }) => {
  if (!bills.length) {
    return (
      <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
        <Receipt className="h-8 w-8 mx-auto text-slate-300 mb-2" />
        <div className="text-sm text-slate-500">No bills from this supplier yet.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-4 py-2.5">Invoice #</th>
            <th className="text-left px-4 py-2.5 hidden sm:table-cell">Issued</th>
            <th className="text-left px-4 py-2.5 hidden sm:table-cell">Due</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-right px-4 py-2.5">Total</th>
            <th className="px-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {bills.map(b => (
            <tr key={b.id} className="hover:bg-slate-50" data-testid={`bill-row-${b.id}`}>
              <td className="px-4 py-2.5 font-mono text-xs">
                <Link to={`/admin/bills/${b.id}`} className="text-slate-900 hover:underline">
                  {b.invoiceNumber || b.xeroInvoiceNumber || b.id.slice(0, 8)}
                </Link>
              </td>
              <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">{fmtDate(b.issueDate)}</td>
              <td className="px-4 py-2.5 hidden sm:table-cell text-slate-600">{fmtDate(b.dueDate)}</td>
              <td className="px-4 py-2.5">
                <BillStatusBadge status={b.status} xeroStatus={b.xeroStatus} />
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtMoney(b.total)}</td>
              <td className="px-2 py-2.5 text-right">
                <Link to={`/admin/bills/${b.id}`} className="text-slate-400 hover:text-slate-900">
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const BillStatusBadge = ({ status, xeroStatus }) => {
  const xs = (xeroStatus || '').toUpperCase();
  if (xs === 'PAID') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Paid</Badge>;
  if (xs === 'AUTHORISED') return <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">Authorised</Badge>;
  if (status === 'pushed') return <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">Pushed</Badge>;
  if (status === 'dismissed') return <Badge variant="outline" className="text-slate-500">Dismissed</Badge>;
  return <Badge variant="outline">Draft</Badge>;
};

export default SupplierDetail;
